import { SparklesIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { ChatErrorBubble } from "~/ai-chat/ui/chat-error-bubble";
import { ChatMessageBubble, ChatMessageTypingBubble } from "~/ai-chat/ui/chat-message-bubble";
import { ChatSuggestionChip } from "~/ai-chat/ui/chat-suggestion-chip";

const SUGGESTIONS = ["What can you do?", "How do I share a model?", "Find recent activity"];

interface AiChatTranscriptProps {
  isPending: boolean;
  activity?: string | null;
  onSuggestionSelect: (suggestion: string) => void;
}

export function AiChatTranscript({ isPending, activity, onSuggestionSelect }: AiChatTranscriptProps) {
  const { messages } = useAiChat();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, isPending]);

  if (messages.length === 0 && !isPending) {
    return <AiChatEmptyState onSuggestionSelect={onSuggestionSelect} />;
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
      {messages.map((item) => {
        if (item.kind === "error") {
          return <ChatErrorBubble key={item.id} error={item} />;
        }
        return <ChatMessageBubble key={item.id} message={item} />;
      })}
      {isPending && <ChatMessageTypingBubble label={activity ?? undefined} />}
      <div ref={bottomRef} />
    </div>
  );
}

function AiChatEmptyState({ onSuggestionSelect }: { onSuggestionSelect: (suggestion: string) => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
      <SparklesIcon className="text-muted-foreground size-6" />
      <div className="space-y-1">
        <p className="text-sm font-medium">Chat with Istari AI</p>
        <p className="text-muted-foreground text-xs">Ask anything about your resources, models, or platform.</p>
      </div>
      <div className="flex flex-wrap justify-center gap-1.5">
        {SUGGESTIONS.map((suggestion) => (
          <ChatSuggestionChip key={suggestion} label={suggestion} onClick={() => onSuggestionSelect(suggestion)} />
        ))}
      </div>
    </div>
  );
}
